<script setup lang="ts">
/* ===========================================================================
   ui-delta — SidePanel
   ---------------------------------------------------------------------------
   ADDS:      A panel anchored to the right edge that leaves the page usable:
              no scrim, no blur, no focus trap. The page behind stays readable
              and clickable, so a reader can compare the two.
   UPSTREAM:  @ui `Drawer` is modal: it covers the page with a blurred scrim,
              closes on a click outside, and paints a fixed white background
              that cannot theme.
   AT MERGE:  Offer it to @ui as Drawer's non-modal variant (`modal: false`),
              themed with the semantic tokens of theme.css.
   ---------------------------------------------------------------------------
   30rem (--width-sm) wide from tablet up, the full width on a phone. Escape
   closes it. The header slot names the panel; `label` is its accessible name.
   =========================================================================== */
import { onBeforeUnmount, watch } from 'vue'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
  /** What a screen reader announces the panel as. */
  label: string
}>()

const open = defineModel<boolean>({ required: true })

const onKey = (event: KeyboardEvent): void => {
  if (event.key === 'Escape' && open.value) open.value = false
}

watch(
  open,
  (isOpen) => {
    if (isOpen) window.addEventListener('keydown', onKey)
    else window.removeEventListener('keydown', onKey)
  },
  { immediate: true },
)
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
</script>

<template>
  <Teleport to="body">
    <transition name="side-panel">
      <aside v-if="open" class="side-panel" role="dialog" aria-modal="false" :aria-label="props.label" v-bind="$attrs">
        <header class="side-panel__header">
          <slot name="header" />
        </header>
        <div class="side-panel__body">
          <slot />
        </div>
        <footer v-if="$slots.footer" class="side-panel__footer">
          <slot name="footer" />
        </footer>
      </aside>
    </transition>
  </Teleport>
</template>

<style scoped>
.side-panel {
  position: fixed;
  inset-block: 0;
  inset-inline-end: 0;
  /* Above the app header (100), below modals and notifications (1000+), which may open from inside it. */
  z-index: 900;
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--surface-panel);
  border-inline-start: 1px solid var(--line-subtle);
  box-shadow: var(--shadow-modal);
  color: var(--fg-primary);
}

@media (min-width: 768px) {
  .side-panel {
    width: var(--width-sm);
  }
}

.side-panel__header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg) var(--spacing-xl);
  border-block-end: 1px solid var(--line-subtle);
}

.side-panel__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--spacing-xl);
}

.side-panel__footer {
  padding: var(--spacing-lg) var(--spacing-xl);
  border-block-start: 1px solid var(--line-subtle);
}

.side-panel-enter-active,
.side-panel-leave-active {
  transition: transform 0.25s ease;
}

.side-panel-enter-from,
.side-panel-leave-to {
  transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
  .side-panel-enter-active,
  .side-panel-leave-active {
    transition: none;
  }
}
</style>
